<template>
  <div class="space-y-6 p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
    <!-- Header -->
    <div class="border-b border-gray-200 pb-4">
      <h3 class="text-lg font-semibold text-gray-900">
        {{ isNew ? 'Create New Question' : 'Edit Question' }}
      </h3>
      <p class="text-sm text-gray-600 mt-1">
        {{ isNew ? 'Add a new question to this dimension' : 'Modify the question properties' }}
      </p>
    </div>

    <!-- Question Properties -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Dimension Information -->
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Dimension Name
          </label>
          <InputText v-model="localQuestion.properties.dimensionName" class="w-full"
            :class="{ 'p-invalid': !localQuestion.properties.dimensionName }"
             placeholder="Enter dimension name" />
          <small v-if="!localQuestion.properties.dimensionName" class="p-error">
            Dimension name is required
          </small>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Dimension Identifier
          </label>
          <InputText v-model="localQuestion.properties.dimension" class="w-full"
            :class="{ 'p-invalid': !localQuestion.properties.dimension }"
             placeholder="Enter dimension identifier" />
          <small v-if="!localQuestion.properties.dimension" class="p-error">
            Dimension identifier is required
          </small>
        </div>
      </div>

      <!-- Question Content -->
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Question Progress Text (EN)
          </label>
          <InputText v-model="localQuestion.properties.content.text_questionProgress.en"
           class="w-full"
            placeholder="Enter progress text in English" />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Question Progress Text (DE)
          </label>
          <InputText v-model="localQuestion.properties.content.text_questionProgress.de"
           class="w-full"
            placeholder="Enter progress text in German" />
        </div>
      </div>
    </div>

    <!-- Question Text Section -->
    <div class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Question Text (EN)
        </label>
        <InputText v-model="localQuestion.properties.content.text_question.en" class="w-full"
          :class="{ 'p-invalid': !localQuestion.properties.content.text_question.en }"
          placeholder="Enter question text in English" />
        <small v-if="!localQuestion.properties.content.text_question.en" class="p-error">
          Question text in English is required
        </small>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Question Text (DE)
        </label>
        <InputText v-model="localQuestion.properties.content.text_question.de" class="w-full"
          :class="{ 'p-invalid': !localQuestion.properties.content.text_question.de }"
          placeholder="Enter question text in German" />
        <small v-if="!localQuestion.properties.content.text_question.de" class="p-error">
          Question text in German is required
        </small>
      </div>
    </div>

    <!-- Class and Type Selection -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Platform Classes -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Platform Class
        </label>
        <div class="flex items-center space-x-2 mb-2">
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            :class="localQuestion.class ? 'bg-green-100 text-green-800'
             : 'bg-gray-100 text-gray-800'">
            <i class="pi pi-tag mr-1"></i>
            {{ localQuestion.class || 'Not Selected' }}
          </span>
        </div>
        <Dropdown v-model="localQuestion.class"
         :options="platformClassesOptions" option-label="value"
          option-value="value" placeholder="Select a platform class" class="w-full"
          :class="{ 'p-invalid': !localQuestion.class }" />
        <small v-if="!localQuestion.class" class="p-error">
          Platform class is required
        </small>
      </div>

      <!-- Question Types -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">
          Question Type
        </label>
        <div class="flex items-center space-x-2 mb-2">
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            :class="localQuestion.type ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'">
            <i class="pi pi-question-circle mr-1"></i>
            {{ localQuestion.type || 'Not Selected' }}
          </span>
        </div>
        <Dropdown v-model="localQuestion.type" :options="questionTypesOptions"
         option-label="value" option-value="value"
          placeholder="Select a question type" class="w-full"
           :class="{ 'p-invalid': !localQuestion.type }" />
        <small v-if="!localQuestion.type" class="p-error">
          Question type is required
        </small>
      </div>
    </div>

    <!-- Force Input Option -->
    <div class="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-1">
      <div class="flex items-center space-x-3">
        <Checkbox v-model="localQuestion.properties.options.forceInput"
         :binary="true" input-id="forceInput" />
        <label for="forceInput" class="text-sm font-medium text-gray-700">
          Require user input for this question
        </label>
      </div>
      <small class="text-gray-500 block pl-9 text-xs">
        Sticky rotation does not require an answer unless this is enabled.
      </small>
    </div>

    <!-- Question Polarity (Positive/Negative Flag) -->
    <div class="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
      <Checkbox v-model="localQuestion.properties.data.positive"
       :binary="true" input-id="positiveFlag" />
      <label for="positiveFlag" class="text-sm font-medium text-gray-700">
        This is a positive question statement
      </label>
      <small class="text-gray-500 ml-2">
        (Unchecked = negative question statement)
      </small>
    </div>

    <!-- Sticky Question Flag -->
    <div class="flex items-center space-x-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
      <Checkbox v-model="localQuestion.sticky"
       :binary="true" input-id="stickyFlag" />
      <label for="stickyFlag" class="text-sm font-medium text-gray-700">
        Sticky question (included in every rotation)
      </label>
      <small class="text-gray-500 ml-2">
        (Sticky questions are added on top of the rotation count)
      </small>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
      <Button label="Cancel" icon="pi pi-times"
       class="p-button-text p-button-secondary" @click="cancel" />
      <Button v-if="isNew" label="Create Question"
       icon="pi pi-plus" :loading="loading" @click="saveNew" />
      <Button v-else label="Save Changes" icon="pi pi-check" :loading="loading" @click="save" />
      <Button v-if="!isNew" label="Delete Question" icon="pi pi-trash" severity="danger"
       class="p-button-outlined"
        @click="deleteQuestion" />
    </div>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'QuestionEditor',
  components: {
    InputText,
    Button,
    Dropdown,
    Checkbox,
  },
  emits: ['save', 'save-new', 'cancel', 'delete-question'],
  props: {
    question: {
      type: Object,
      required: true,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'edit', // 'create', 'edit', 'view'
      validator: (value) => ['create', 'edit', 'view'].includes(value),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Use a local copy for editing.
      localQuestion: JSON.parse(JSON.stringify(this.question)),
    };
  },
  computed: {
    ...mapGetters('enums', { getEnumByName: 'getEnumTypeByName' }),
    platformClassesOptions() {
      const enumObj = this.getEnumByName('platformClasses');
      // Expecting enumObj.values to be an array of objects.
      // We assume each value is an object with a "value" property.
      return enumObj && enumObj.values ? enumObj.values : [];
    },
    questionTypesOptions() {
      const enumObj = this.getEnumByName('questionTypes');
      return enumObj && enumObj.values ? enumObj.values : [];
    },
  },
  mounted() {
    // Fetch the tasks when the component is mounted.
    this.fetchEnumTypes();
  },
  methods: {
    ...mapActions('enums', ['fetchEnumTypes']),

    validateQuestion() {
      const errors = [];

      if (!this.localQuestion.properties.dimensionName?.trim()) {
        errors.push('Dimension Name');
      }
      if (!this.localQuestion.properties.dimension?.trim()) {
        errors.push('Dimension Identifier');
      }
      if (!this.localQuestion.properties.content.text_question.en?.trim()) {
        errors.push('Question Text (EN)');
      }
      if (!this.localQuestion.properties.content.text_question.de?.trim()) {
        errors.push('Question Text (DE)');
      }
      if (!this.localQuestion.class) {
        errors.push('Platform Class');
      }
      if (!this.localQuestion.type) {
        errors.push('Question Type');
      }
      if (this.localQuestion.properties.data?.positive === undefined
       || this.localQuestion.properties.data?.positive === null) {
        errors.push('Question Polarity (Positive/Negative flag)');
      }

      return errors;
    },

    save() {
      const errors = this.validateQuestion();
      if (errors.length > 0) {
        this.$toast?.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: `Missing required fields: ${errors.join(', ')}`,
          life: 5000,
        });
        return;
      }
      // Emit the updated question to the parent.
      this.$emit('update-question', this.localQuestion);
    },

    async saveNew() {
      const errors = this.validateQuestion();
      if (errors.length > 0) {
        this.$toast?.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: `Missing required fields: ${errors.join(', ')}`,
          life: 5000,
        });
        return;
      }
      // Emit the new question data to the parent component
      this.$emit('save-new', this.localQuestion);
    },

    cancel() {
      this.$emit('cancel');
    },

    deleteQuestion() {
      // Emit a delete event to the parent.
      this.$emit('delete-question');
    },
  },
  watch: {
    question: {
      handler(newVal) {
        this.localQuestion = JSON.parse(JSON.stringify(newVal));
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
